import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import config from 'config/env';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useDispatch, useSelector } from 'react-redux';
import { NavItem } from './components';
import { Divider, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Building2, Contact, FileBarChart } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import useHandleTrackEvent from '@/hooks/useHandleTrackEvent';
import { updateUiState } from 'redux/slices/uiSlice';

import DynamicIcon from 'components/@2025/DynamicIcon';
import ViewAsBanner from '../ViewAsBanner';
import SidebarAccountRow from '../SidebarAccountRow';
import { RAIL_LABELS } from '../../navGroups';

/**
 * Scrolls the nav's scroller to a group's `data-nav-group` anchor. `ifNeeded`
 * leaves it alone when the group's heading is already in view, so a route
 * change from a click inside the nav does not yank the list around under the
 * pointer; the rail's explicit picks use `start`.
 */
const scrollToGroup = (scroller, { key, mode, smooth }) => {
  const anchor = scroller?.querySelector(`[data-nav-group="${key}"]`);
  if (!anchor) return;
  const scrollerTop = scroller.getBoundingClientRect().top;
  const anchorTop = anchor.getBoundingClientRect().top - scrollerTop;
  if (
    mode === 'ifNeeded' &&
    anchorTop >= 0 &&
    anchorTop < scroller.clientHeight - 48
  ) {
    return;
  }
  scroller.scrollTo({
    top: scroller.scrollTop + anchorTop - 4,
    behavior: smooth ? 'smooth' : 'auto',
  });
};

const SidebarNav = ({
  onClose,
  expanded,
  twoLevel = false,
  scrollRequest,
  rail = false,
  selectedGroup,
  onSelectGroup,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const isAskAiListView = new URLSearchParams(search).get('list') === 'true';
  const [, setActiveLink] = useState('');
  const dashboardSidebarExpanded = useSelector(
    (state) => state.uiStates.dashboardSidebarExpanded,
  );
  const unassociatedItemsCount = useSelector(
    (state) => state.uiStates.unassociatedItemsCount,
  );
  const mailboxUnrepliedCount = useSelector(
    (state) => state.uiStates.mailboxUnrepliedCount,
  );
  const iconsLibrary = useSelector((state) => state.uiStates.iconsLibrary);
  const scrollerRef = useRef(null);
  const selectedDealsLabel = useSelector(
    (state) => state.uiStates.selectedDealsLabel,
  );
  const isSm = useMediaQuery(theme.breakpoints.down('sm'), {
    defaultMatches: false,
  });
  const userDetails = useSelector((state) => state.userDetails.userDetails);
  const { orgConfigs } = useSelector((state) => state.org.orgConfigs);
  const isViewAsMode = useSelector((state) => state.auth?.isViewAsMode);
  const { deals } = useSelector((state) => state.deals.pinnedDeals);
  const allTeamMembers = useSelector((state) => state.org.teamMembers);

  const isAdmin =
    userDetails?.orgRoles?.[0] === 'ORG_ADMIN' ||
    userDetails?.teamRoles?.[0] === 'TEAM_ADMIN';
  const hasMultipleUsers = allTeamMembers?.members?.length > 1;
  const showRecorder = isAdmin && (config.isLocal || config.isDevelopment);
  const toolsActive = /\/unlinked-items|\/companies|\/contacts|\/m-record/i.test(
    pathname,
  );
  // With the sidebar pinned collapsed (Preferences), there is no toggle. The
  // pin applies only to the two-level nav; the one-level sidebar keeps it.
  const navCollapsedByDefault = useSelector(
    (state) => state.uiStates.navCollapsedByDefault,
  );
  const twoLevelNav = useSelector((state) => state.uiStates.twoLevelNav);
  const pinnedCollapsed =
    Boolean(twoLevelNav) && navCollapsedByDefault !== false;

  const handleTrackEvent = useHandleTrackEvent();

  // `rail`: this nav rendered as the two-level sidebar's first level -- the
  // same groups, gated the same way, each as an icon-and-label tile. Only the
  // groups render; the logo, account row and View As notice are the rail's.
  const bare = twoLevel || rail;
  const railProps = (key) =>
    rail
      ? {
          variant: 'rail',
          railLabel: RAIL_LABELS[key],
          railSelected: selectedGroup === key,
          onRailSelect: () => onSelectGroup?.(key),
        }
      : {};

  useEffect(() => {
    setActiveLink(window && window.location ? window.location.pathname : '');
  }, []);

  // The two-level sidebar's rail asks for a group; this also runs on mount,
  // so a panel that opens because a rail group was picked lands on it.
  useEffect(() => {
    if (scrollRequest?.key) {
      scrollToGroup(scrollerRef.current, scrollRequest);
    }
  }, [scrollRequest]);

  return (
    <Box
      height={1}
      display={'flex'}
      flexDirection={'column'}
      position={'relative'}
    >
      {/* Two-level: the logo and the collapse toggle live on the rail. */}
      {bare ? null : (
        <Box display={'flex'} flexDirection={'column'}>
          <Box
            width={1}
            paddingX={2}
            paddingY={1}
            display={'flex'}
            flexDirection={expanded ? 'row' : 'column'}
            justifyContent={'space-between'}
            alignItems={'center'}
            gap={2}
            sx={{}}
          >
            <Box
              data-trackid={'logo_clicked'}
              display={'flex'}
              component="a"
              href={'https://dealcopilot.ai/'}
              title={'Deal Copilot'}
              width={32}
              onClick={(e) => {
                handleTrackEvent(e, 'logo_clicked');
              }}
            >
              <Box
                component={'img'}
                src={'/dh-icons/logo.svg'}
                height={1}
                width={1}
                sx={{ filter: 'var(--dc-logo-filter)' }}
              />
            </Box>

            {isSm ? (
              <Button
                onClick={() => onClose()}
                aria-label="Menu"
                variant={'text'}
                sx={{
                  minWidth: 'auto',
                  padding: 0,
                }}
              >
                <Close />
              </Button>
            ) : pinnedCollapsed ? null : (
              <Tooltip
                title={
                  dashboardSidebarExpanded
                    ? 'Collapse sidebar'
                    : 'Expand sidebar'
                }
                arrow
                placement="right"
              >
                <IconButton
                  data-tour="sidebar-toggle"
                  data-trackid={'toggle_sidebar_expand'}
                  aria-label={
                    dashboardSidebarExpanded
                      ? 'Collapse sidebar'
                      : 'Expand sidebar'
                  }
                  size="small"
                  onClick={(e) => {
                    handleTrackEvent(e, 'toggle_sidebar_expand');
                    dispatch(
                      updateUiState({
                        key: 'dashboardSidebarExpanded',
                        value: !dashboardSidebarExpanded,
                      }),
                    );
                    // Remembered so a reload keeps the sidebar as it was left.
                    dispatch(
                      updateUiState({
                        key: 'navSidebarExpanded',
                        value: !dashboardSidebarExpanded,
                      }),
                    );
                  }}
                >
                  <DynamicIcon
                    type={iconsLibrary || 'lucide'}
                    name={
                      dashboardSidebarExpanded
                        ? 'toggleSidebarCollapse'
                        : 'toggleSidebarExpand'
                    }
                    color={theme.palette.text.secondary}
                    width={20}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Divider
            orientation="horizontal"
            sx={{
              width: '100%',
              height: '1px',
            }}
          />
        </Box>
      )}
      {bare ? null : <ViewAsBanner />}
      <Box
        ref={scrollerRef}
        sx={{
          // This used to be a pair of `calc(100vh - <magic>)` bounds that had
          // to be kept in step with the height of whatever sat below it. The
          // footer is now a variable-height block (secondary links plus the
          // account row), so the scroller just takes the leftover column
          // instead. `minHeight: 0` is what lets a flex child actually shrink
          // and scroll rather than growing to fit its content.
          flexGrow: 1,
          minHeight: 0,
          paddingX: rail ? 0.75 : 1,
          paddingY: rail ? 0 : 1,
          overflowY: 'auto',
          // Vertical only. With just `overflowY` set, the browser makes the
          // x axis scrollable too, and a row a pixel wider than the column --
          // a count chip, a long deal name -- put a horizontal bar under the
          // nav. Rows truncate with an ellipsis instead.
          overflowX: 'hidden',
          // The thumb is painted at rest, not just while scrolling, so the nav
          // says up front that it runs past the fold.
          //
          // Deliberately webkit-only: `scrollbar-width`/`scrollbar-color` are
          // the standard properties, but Chromium ignores every
          // `::-webkit-scrollbar-*` rule on an element that sets either of
          // them, and what it gives back on macOS is an overlay scrollbar that
          // fades out when idle -- i.e. the behaviour this is replacing. The
          // pseudo-element rules force a classic, always-present scrollbar.
          // Firefox has no way to opt out of the overlay, so it gets the
          // standard properties on their own, scoped by a Firefox-only
          // `@supports` probe that Chromium and Safari both fail.
          // The rail is too narrow to give up a gutter; it scrolls without a
          // visible bar.
          '&::-webkit-scrollbar': {
            width: rail ? 0 : '8px',
          },
          '& [data-rail-group] + [data-rail-group]': { marginTop: 0.5 },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            // The app-wide scrollbar token, so the rail matches every other
            // bar and follows the theme panel's per-mode setting.
            backgroundColor: 'var(--dc-scrollbar)',
            // The gutter is 8px but the thumb reads as 4px: a transparent
            // border clipped to the content box insets it off the nav rows.
            border: '2px solid transparent',
            backgroundClip: 'content-box',
            borderRadius: '4px',
          },
          '@supports (-moz-appearance: none)': {
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--dc-scrollbar) transparent',
          },
        }}
      >
        <Box>
          {/* Deal Copilot Menu starts here*/}

          {/* ORG ADMIN */}
          {userDetails?.orgRoles?.[0] === 'ORG_ADMIN' ||
          userDetails?.teamRoles?.[0] === 'TEAM_ADMIN' ||
          userDetails?.teamRoles?.[0] === 'STANDARD' ? (
            <Box>
              <NavItem
                navGroup="deals"
                {...railProps('deals')}
                id="dealsLeftNav"
                data-trackid={'setup'}
                dataTour="sidebar-deals"
                title={' Deals'}
                items={[
                  {
                    name: ` ${selectedDealsLabel || 'All Deals'}`,
                    href: `/deals`,
                    startIcon: '',
                    adminTooltip:
                      isAdmin && hasMultipleUsers
                        ? 'You can view user specific deals by navigating to this page'
                        : '',
                  },
                  ...([...(deals || [])]
                    ?.sort((a, b) => {
                      if (a?.archived && !b?.archived) {
                        return 1;
                      } else if (!a?.archived && b?.archived) {
                        return -1;
                      } else {
                        return 0;
                      }
                    })
                    .map((deal) => {
                      return {
                        name: deal?.name,
                        href: `/deals/${deal?.id}`,
                        startIcon: '',
                        // startIcon: deal?.archived ? (
                        //   <Circle
                        //     style={{
                        //       color: theme.palette.text.disabled,
                        //       width: 18,
                        //     }}
                        //   />
                        // ) : (
                        //   <Box
                        //     sx={{
                        //       width: 18,
                        //       height: 18,
                        //       display: 'flex',
                        //       alignItems: 'center',
                        //       justifyContent: 'center',
                        //       position: 'relative',
                        //     }}
                        //   >
                        //     <Circle
                        //       style={{
                        //         color: theme.palette.text.disabled,
                        //         width: 18,
                        //       }}
                        //     />
                        //     <CircleSmall
                        //       style={{
                        //         color: theme.palette.text.disabled,
                        //         width: 14,
                        //         position: 'absolute',
                        //         fill: theme.palette.text.disabled,
                        //       }}
                        //     />
                        //   </Box>
                        // ),
                        highlighted: new RegExp(`/deals/${deal?.id}`, 'i').test(
                          pathname,
                        ),
                        archived: deal?.archived,
                        // A pinned deal hangs off the All Deals row above it
                        // rather than off the group, so it renders one level
                        // in, behind an indent guide.
                        nested: true,
                      };
                    }) || []),
                ]}
                href={'/deals'}
                highlighted={new RegExp(`/deals`, 'i').test(pathname)}
                startIcon={
                  <DynamicIcon
                    type={iconsLibrary || 'lucide'}
                    name="deals"
                    color={
                      new RegExp(`/deals`, 'i').test(pathname)
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.primary
                    }
                    invert={
                      new RegExp(`/deals`, 'i').test(pathname)
                        ? 'invert(100%)'
                        : 'none'
                    }
                    width={20}
                  />
                }
                onClick={(e) => {
                  handleTrackEvent(e, 'deals');
                }}
              />
              <NavItem
                navGroup="mailbox"
                {...railProps('mailbox')}
                standalone
                data-trackid={'mailbox'}
                dataTour="sidebar-mailbox"
                title={'Mailbox'}
                items={[]}
                href={'/mailbox'}
                showCounts={mailboxUnrepliedCount || null}
                highlighted={new RegExp(`/mailbox`, 'i').test(pathname)}
                startIcon={
                  <DynamicIcon
                    type={iconsLibrary || 'lucide'}
                    name="mailbox"
                    color={
                      new RegExp(`/mailbox`, 'i').test(pathname)
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.primary
                    }
                    invert={
                      new RegExp(`/mailbox`, 'i').test(pathname)
                        ? 'invert(100%)'
                        : 'none'
                    }
                    width={20}
                  />
                }
                onClick={(e) => {
                  handleTrackEvent(e, 'mailbox');
                }}
              />
              <NavItem
                navGroup="actionPlan"
                {...railProps('actionPlan')}
                standalone
                data-trackid={'action-plan'}
                dataTour="sidebar-action-plan"
                title={'Action Plan'}
                items={[]}
                href={'/action-plan'}
                highlighted={new RegExp(`/action-plan`, 'i').test(pathname)}
                startIcon={
                  <DynamicIcon
                    type={iconsLibrary || 'lucide'}
                    name="actionPlan"
                    color={
                      new RegExp(`/action-plan`, 'i').test(pathname)
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.primary
                    }
                    invert={
                      new RegExp(`/action-plan`, 'i').test(pathname)
                        ? 'invert(100%)'
                        : 'none'
                    }
                    width={20}
                  />
                }
                onClick={(e) => {
                  handleTrackEvent(e, 'action-plan');
                }}
              />
              {orgConfigs?.generateWeeklyDealSummary ? (
                <NavItem
                  navGroup="reports"
                  {...railProps('reports')}
                  standalone
                  data-trackid={'reports'}
                  dataTour="sidebar-reports"
                  title={'Reports'}
                  items={[]}
                  href={'/reports'}
                  highlighted={new RegExp(`/reports`, 'i').test(pathname)}
                  startIcon={
                    <FileBarChart
                      size={20}
                      style={{
                        color: new RegExp(`/reports`, 'i').test(pathname)
                          ? theme.palette.primary.contrastText
                          : theme.palette.text.primary,
                      }}
                    />
                  }
                  onClick={(e) => {
                    handleTrackEvent(e, 'reports');
                  }}
                />
              ) : null}
              <NavItem
                navGroup="tools"
                {...railProps('tools')}
                id="actionsRequiredLeftNav"
                data-trackid={'actionsRequired'}
                dataTour="sidebar-tools"
                title={' Tools'}
                items={[
                  {
                    name: `Unlinked Items`,
                    href: `/unlinked-items`,
                    dataTour: 'sidebar-unlinked-items',
                    startIcon: (
                      <DynamicIcon
                        type={iconsLibrary || 'lucide'}
                        name="unlinkedItems"
                        color={
                          new RegExp(`/unlinked-items`, 'i').test(pathname)
                            ? theme.palette.primary.contrastText
                            : theme.palette.text.primary
                        }
                        invert={
                          new RegExp(`/unlinked-items`, 'i').test(pathname)
                            ? 'invert(100%)'
                            : 'none'
                        }
                        width={20}
                      />
                    ),
                    highlighted: new RegExp(`/unlinked-items`, 'i').test(
                      pathname,
                    ),
                    showCounts: unassociatedItemsCount,
                  },
                  {
                    name: 'Companies',
                    href: '/companies',
                    dataTour: 'sidebar-companies',
                    startIcon: (
                      <Building2
                        size={20}
                        style={{
                          color: new RegExp(`/companies`, 'i').test(pathname)
                            ? theme.palette.primary.contrastText
                            : theme.palette.text.primary,
                        }}
                      />
                    ),
                    highlighted: new RegExp(`/companies`, 'i').test(pathname),
                  },
                  {
                    name: 'Contacts',
                    href: '/contacts',
                    dataTour: 'sidebar-contacts',
                    startIcon: (
                      <Contact
                        size={20}
                        style={{
                          color: new RegExp(`/contacts`, 'i').test(pathname)
                            ? theme.palette.primary.contrastText
                            : theme.palette.text.primary,
                        }}
                      />
                    ),
                    highlighted: new RegExp(`/contacts`, 'i').test(pathname),
                  },
                  // The meeting recorder is dev-only, and admin-only.
                  ...(showRecorder
                    ? [
                        {
                          name: `Meeting Recorder`,
                          href: `/m-record`,
                          startIcon: (
                            <DynamicIcon
                              type={iconsLibrary || 'lucide'}
                              name="mRecord"
                              color={
                                new RegExp(`/m-record`, 'i').test(pathname)
                                  ? theme.palette.primary.contrastText
                                  : theme.palette.text.primary
                              }
                              invert={
                                new RegExp(`/m-record`, 'i').test(pathname)
                                  ? 'invert(100%)'
                                  : 'none'
                              }
                              width={20}
                            />
                          ),
                          highlighted: new RegExp(`/m-record`, 'i').test(
                            pathname,
                          ),
                        },
                      ]
                    : []),
                ]}
                href={'/unlinked-items'}
                highlighted={toolsActive}
                startIcon={
                  <DynamicIcon
                    type={iconsLibrary || 'lucide'}
                    name="tools"
                    color={
                      toolsActive
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.primary
                    }
                    invert={toolsActive ? 'invert(100%)' : 'none'}
                    width={20}
                  />
                }
                onClick={(e) => {
                  handleTrackEvent(e, 'tools');
                }}
              />
              {userDetails?.orgRoles?.[0] === 'ORG_ADMIN' ||
              userDetails?.teamRoles?.[0] === 'TEAM_ADMIN' ? (
                <NavItem
                  navGroup="askAi"
                  {...railProps('askAi')}
                  data-trackid={'expertBot'}
                  dataTour="sidebar-ask-ai"
                  title={' Ask AI'}
                  items={
                    isViewAsMode
                      ? [
                          {
                            name: `Previous Chats`,
                            href: `/ask-ai?list=true`,
                            startIcon: (
                              <DynamicIcon
                                type={iconsLibrary || 'lucide'}
                                name="chats"
                                color={
                                  new RegExp(`/ask-ai`, 'i').test(pathname)
                                    ? theme.palette.primary.contrastText
                                    : theme.palette.text.primary
                                }
                                invert={
                                  new RegExp(`/ask-ai`, 'i').test(pathname)
                                    ? 'invert(100%)'
                                    : 'none'
                                }
                                width={20}
                              />
                            ),
                          },
                          // {
                          //   name: 'Talk to Expert',
                          //   href: `/ask-ai?type=talk`,
                          //   startIcon: '',
                          //   disabled: userInfo?.orgRoles?.includes('SS_ADMIN'),
                          // },
                        ]
                      : [
                          {
                            name: 'New Chat',
                            href: `/ask-ai`,
                            startIcon: (
                              <DynamicIcon
                                type={iconsLibrary || 'lucide'}
                                name="askAI"
                                color={
                                  new RegExp(`/ask-ai`, 'i').test(pathname) &&
                                  !isAskAiListView
                                    ? theme.palette.primary.contrastText
                                    : theme.palette.text.primary
                                }
                                invert={
                                  new RegExp(`/ask-ai`, 'i').test(pathname) &&
                                  !isAskAiListView
                                    ? 'invert(100%)'
                                    : 'none'
                                }
                                width={20}
                              />
                            ),
                            onClick: () => {
                              dispatch(
                                updateUiState({
                                  key: 'askExpertResetKey',
                                  value: Date.now(),
                                }),
                              );
                            },
                          },
                          {
                            name: `Previous Chats`,
                            href: `/ask-ai?list=true`,
                            startIcon: (
                              <DynamicIcon
                                type={iconsLibrary || 'lucide'}
                                name="chats"
                                color={
                                  new RegExp(`/ask-ai`, 'i').test(pathname) &&
                                  isAskAiListView
                                    ? theme.palette.primary.contrastText
                                    : theme.palette.text.primary
                                }
                                invert={
                                  new RegExp(`/ask-ai`, 'i').test(pathname) &&
                                  isAskAiListView
                                    ? 'invert(100%)'
                                    : 'none'
                                }
                                width={20}
                              />
                            ),
                          },
                          // {
                          //   name: 'Talk to Expert',
                          //   href: `/ask-ai?type=talk`,
                          //   startIcon: '',
                          //   disabled: userInfo?.orgRoles?.includes('SS_ADMIN'),
                          // },
                        ]
                  }
                  href={'/ask-ai'}
                  highlighted={new RegExp(`/ask-ai`, 'i').test(pathname)}
                  startIcon={
                    <DynamicIcon
                      type={iconsLibrary || 'lucide'}
                      name="askAI"
                      color={
                        new RegExp(`/ask-ai`, 'i').test(pathname)
                          ? theme.palette.primary.contrastText
                          : theme.palette.text.primary
                      }
                      invert={
                        new RegExp(`/ask-ai`, 'i').test(pathname)
                          ? 'invert(100%)'
                          : 'none'
                      }
                      width={20}
                    />
                  }
                  onClick={(e) => {
                    handleTrackEvent(e, 'ask-ai');
                  }}
                  onNavClick={() => {
                    dispatch(
                      updateUiState({
                        key: 'askExpertResetKey',
                        value: Date.now(),
                      }),
                    );
                  }}
                />
              ) : null}
              {/* ORG ADMIN */}
              {(userDetails?.orgRoles?.[0] === 'ORG_ADMIN' ||
                userDetails?.teamRoles?.[0] === 'TEAM_ADMIN' ||
                userDetails?.orgRoles?.[0] === 'WL_ORG_ADMIN') &&
              userDetails?.custom?.orgType !== 'demo_org' ? (
                <NavItem
                  navGroup="myOrg"
                  {...railProps('myOrg')}
                  data-trackid={'teams'}
                  dataTour="sidebar-my-organization"
                  title={' My Organization'}
                  items={[
                    {
                      name: `All Members`,
                      href: `/manage-members`,
                      dataTour: 'sidebar-manage-members',
                      startIcon: (
                        <DynamicIcon
                          type={iconsLibrary || 'lucide'}
                          name="allMembers"
                          color={
                            new RegExp(`/manage-members`, 'i').test(pathname)
                              ? theme.palette.primary.contrastText
                              : theme.palette.text.primary
                          }
                          invert={
                            new RegExp(`/manage-members`, 'i').test(pathname)
                              ? 'invert(100%)'
                              : 'none'
                          }
                          width={20}
                        />
                      ),
                      highlighted: new RegExp(`/manage-members`, 'i').test(
                        pathname,
                      ),
                    },
                    {
                      name: `Sales Org`,
                      href: `/sales-org`,
                      dataTour: 'sidebar-sales-org',
                      startIcon: (
                        <DynamicIcon
                          type={iconsLibrary || 'lucide'}
                          name="salesOrg"
                          color={
                            new RegExp(`/sales-org`, 'i').test(pathname)
                              ? theme.palette.primary.contrastText
                              : theme.palette.text.primary
                          }
                          invert={
                            new RegExp(`/sales-org`, 'i').test(pathname)
                              ? 'invert(100%)'
                              : 'none'
                          }
                          width={20}
                        />
                      ),
                      highlighted: new RegExp(`/sales-org`, 'i').test(pathname),
                    },
                    {
                      name: `Org Data`,
                      href: `/org-settings`,
                      dataTour: 'sidebar-org-settings',
                      startIcon: (
                        <DynamicIcon
                          type={iconsLibrary || 'lucide'}
                          name="orgData"
                          color={
                            new RegExp(`/org-settings`, 'i').test(pathname)
                              ? theme.palette.primary.contrastText
                              : theme.palette.text.primary
                          }
                          invert={
                            new RegExp(`/org-settings`, 'i').test(pathname)
                              ? 'invert(100%)'
                              : 'none'
                          }
                          width={20}
                        />
                      ),
                      highlighted: new RegExp(`/org-settings`, 'i').test(
                        pathname,
                      ),
                    },
                  ]}
                  href={'/manage-members'}
                  highlighted={
                    (new RegExp(`/settings`, 'i').test(pathname) &&
                      !new RegExp(`/customer/settings`, 'i').test(pathname)) ||
                    new RegExp(`/pricing`, 'i').test(pathname) ||
                    new RegExp(`/all-teams`, 'i').test(pathname) ||
                    new RegExp(`/team-settings`, 'i').test(pathname) ||
                    new RegExp(`/manage-members`, 'i').test(pathname) ||
                    new RegExp(`/org-settings`, 'i').test(pathname) ||
                    new RegExp(`/sales-org`, 'i').test(pathname)
                  }
                  startIcon={
                    <DynamicIcon
                      type={iconsLibrary || 'lucide'}
                      name="myOrg"
                      color={
                        (new RegExp(`/settings`, 'i').test(pathname) &&
                          !new RegExp(`/customer/settings`, 'i').test(
                            pathname,
                          )) ||
                        new RegExp(`/pricing`, 'i').test(pathname) ||
                        new RegExp(`/all-teams`, 'i').test(pathname) ||
                        new RegExp(`/manage-members`, 'i').test(pathname) ||
                        new RegExp(`/team-settings`, 'i').test(pathname) ||
                        new RegExp(`/org-settings`, 'i').test(pathname) ||
                        new RegExp(`/sales-org`, 'i').test(pathname)
                          ? theme.palette.primary.contrastText
                          : theme.palette.text.primary
                      }
                      invert={
                        (new RegExp(`/settings`, 'i').test(pathname) &&
                          !new RegExp(`/customer/settings`, 'i').test(
                            pathname,
                          )) ||
                        new RegExp(`/pricing`, 'i').test(pathname) ||
                        new RegExp(`/all-teams`, 'i').test(pathname) ||
                        new RegExp(`/manage-members`, 'i').test(pathname) ||
                        new RegExp(`/team-settings`, 'i').test(pathname) ||
                        new RegExp(`/org-settings`, 'i').test(pathname) ||
                        new RegExp(`/sales-org`, 'i').test(pathname)
                          ? 'invert(100%)'
                          : 'none'
                      }
                      width={20}
                    />
                  }
                  onClick={(e) => {
                    handleTrackEvent(e, 'all-teams');
                  }}
                />
              ) : null}
              {/* ORG ADMIN */}
              <NavItem
                navGroup="setup"
                {...railProps('setup')}
                id="setupLeftNav"
                data-trackid={'setup'}
                dataTour="sidebar-setup"
                title={' Setup'}
                items={
                  // config.isLocal ||
                  // config.isDevelopment
                  //   ?
                  [
                    {
                      name: `Integrations`,
                      href: `/integrations`,
                      dataTour: 'sidebar-integrations',
                      startIcon: (
                        <DynamicIcon
                          type={iconsLibrary || 'lucide'}
                          name="integrations"
                          color={
                            new RegExp(`/integrations`, 'i').test(pathname)
                              ? theme.palette.primary.contrastText
                              : theme.palette.text.primary
                          }
                          invert={
                            new RegExp(`/integrations`, 'i').test(pathname)
                              ? 'invert(100%)'
                              : 'none'
                          }
                          width={20}
                        />
                      ),
                    },
                    {
                      name: `Preferences`,
                      href: `/preferences`,
                      dataTour: 'sidebar-preferences',
                      startIcon: (
                        <DynamicIcon
                          type={iconsLibrary || 'lucide'}
                          name="preferences"
                          color={
                            new RegExp(`/preferences`, 'i').test(pathname)
                              ? theme.palette.primary.contrastText
                              : theme.palette.text.primary
                          }
                          invert={
                            new RegExp(`/preferences`, 'i').test(pathname)
                              ? 'invert(100%)'
                              : 'none'
                          }
                          width={20}
                        />
                      ),
                      highlighted: new RegExp(`/preferences`, 'i').test(
                        pathname,
                      ),
                    },
                    {
                      name: `Profile Settings`,
                      href: `/profile`,
                      dataTour: 'sidebar-profile',
                      startIcon: (
                        <DynamicIcon
                          type={iconsLibrary || 'lucide'}
                          name="profile"
                          color={
                            new RegExp(`/profile`, 'i').test(pathname)
                              ? theme.palette.primary.contrastText
                              : theme.palette.text.primary
                          }
                          invert={
                            new RegExp(`/profile`, 'i').test(pathname)
                              ? 'invert(100%)'
                              : 'none'
                          }
                          width={20}
                        />
                      ),
                    },
                  ]
                }
                href={'/integrations'}
                highlighted={
                  new RegExp(`/discovery-template`, 'i').test(pathname) ||
                  new RegExp(`/integrations`, 'i').test(pathname) ||
                  new RegExp(`/preferences`, 'i').test(pathname) ||
                  new RegExp(`/profile`, 'i').test(pathname) ||
                  // new RegExp(`/org-settings`, 'i').test(pathname) ||
                  new RegExp(`/deal-discovery`, 'i').test(pathname)
                }
                startIcon={
                  <DynamicIcon
                    type={iconsLibrary || 'lucide'}
                    name="setup"
                    color={
                      new RegExp(`/discovery-template`, 'i').test(pathname) ||
                      new RegExp(`/integrations`, 'i').test(pathname) ||
                      new RegExp(`/preferences`, 'i').test(pathname) ||
                      new RegExp(`/profile`, 'i').test(pathname) ||
                      // new RegExp(`/org-settings`, 'i').test(pathname) ||
                      new RegExp(`/deal-discovery`, 'i').test(pathname)
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.primary
                    }
                    invert={
                      new RegExp(`/discovery-template`, 'i').test(pathname) ||
                      new RegExp(`/integrations`, 'i').test(pathname) ||
                      new RegExp(`/preferences`, 'i').test(pathname) ||
                      new RegExp(`/profile`, 'i').test(pathname) ||
                      // new RegExp(`/org-settings`, 'i').test(pathname) ||
                      new RegExp(`/deal-discovery`, 'i').test(pathname)
                        ? 'invert(100%)'
                        : 'none'
                    }
                    width={20}
                  />
                }
                onClick={(e) => {
                  handleTrackEvent(e, 'discovery-template');
                }}
              />
            </Box>
          ) : null}
          {/* ORG ADMIN */}

          {/* Deal Copilot Menu ends here*/}
        </Box>
      </Box>
      {bare ? null : (
        <Box marginTop={'auto'} display={'flex'} flexDirection={'column'}>
          <Divider
            orientation="horizontal"
            sx={{
              width: '100%',
              height: '1px',
            }}
          />
          <Box
            display={'flex'}
            alignItems={'center'}
            paddingX={dashboardSidebarExpanded ? 1 : 0.5}
            paddingY={0.5}
          >
            <SidebarAccountRow collapsed={!dashboardSidebarExpanded} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

SidebarNav.propTypes = {
  onClose: PropTypes.func,
  expanded: PropTypes.bool,
  onSidebarClose: PropTypes.func,
  twoLevel: PropTypes.bool,
  rail: PropTypes.bool,
  selectedGroup: PropTypes.string,
  onSelectGroup: PropTypes.func,
  scrollRequest: PropTypes.shape({
    key: PropTypes.string,
    mode: PropTypes.oneOf(['start', 'ifNeeded']),
    smooth: PropTypes.bool,
  }),
};

export default SidebarNav;
